/**
 * Map a hex color to a friendly human name for ink/thread display.
 *
 * Phase 1-A (variant cards wired to real Artwork) — the VariantCard
 * pre-Phase-1A had a 2-entry hardcoded map in the component:
 *   #ffffff -> "White", #0a1b3d/#000000 -> "Navy"
 *
 * Real Artwork rows now arrive with `ink_color_hex` populated by the
 * distributor's seeded library, so the component needs a broader mapping.
 * This is intentionally a SHORT table covering the canonical thread/ink
 * colors a decorator would name on a job ticket — not a full Pantone or
 * 256-color palette. When we don't recognize the hex, we surface the hex
 * itself (uppercased) so the user still sees a deterministic identifier
 * instead of a fabricated name.
 *
 * Lookup is case-insensitive; three-char shorthand (#fff) is expanded
 * before lookup so #fff and #ffffff resolve to the same name.
 */

// Canonical decorator-floor ink/thread palette. Keys are lowercase
// six-digit hex with leading `#`. Values are the spoken name a
// production manager would use on a job ticket.
const HEX_TO_INK_NAME = Object.freeze({
    "#ffffff": "White",
    "#000000": "Black",
    "#0a1b3d": "Navy",
    "#1f2a44": "Navy",
    "#102a4c": "Navy",
    "#c8102e": "Red",
    "#b22234": "Red",
    "#1f7a3a": "Green",
    "#0a6b2c": "Green",
    "#ffd700": "Gold",
    "#c9a227": "Gold",
    "#c0c0c0": "Silver",
    "#808080": "Gray",
    "#1f3a8a": "Royal Blue",
    "#3b82f6": "Royal Blue",
    "#f97316": "Orange",
    "#ea580c": "Orange",
    "#a855f7": "Purple",
    "#7c3aed": "Purple",
    "#ec4899": "Pink",
    "#f59e0b": "Athletic Gold",
    "#5d3a1a": "Brown",
});

/**
 * Normalize a hex string to lowercase `#rrggbb`. Returns `null` for any
 * input that doesn't match the hex shape (callers fall back to the raw
 * input or a placeholder).
 *
 * Accepts `#rgb`, `#RGB`, `#rrggbb`, `#RRGGBB`. Strips a leading `#` if
 * already present, then re-adds one canonical leading `#`.
 */
export function normalizeHex(hex) {
    if (typeof hex !== "string") return null;
    const trimmed = hex.trim();
    if (!trimmed) return null;
    const body = trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
    if (/^[0-9a-fA-F]{3}$/.test(body)) {
        const r = body[0];
        const g = body[1];
        const b = body[2];
        return `#${(r + r + g + g + b + b).toLowerCase()}`;
    }
    if (/^[0-9a-fA-F]{6}$/.test(body)) {
        return `#${body.toLowerCase()}`;
    }
    return null;
}

/**
 * Return the named ink for a hex, or the uppercased canonical hex when
 * the color isn't in the canonical table. Returns `null` when the hex
 * itself is malformed so callers can render an em-dash or skip the row.
 *
 * @param {string|null|undefined} hex - hex string (with or without `#`).
 * @returns {string|null} the friendly name, or the canonical uppercase
 *   hex when unrecognized, or `null` when the input isn't a valid hex.
 */
export function inkColorName(hex) {
    const normalized = normalizeHex(hex);
    if (normalized == null) return null;
    const named = HEX_TO_INK_NAME[normalized];
    if (named) return named;
    // Unknown but valid hex: show the canonical uppercase form so the
    // user has a stable identifier rather than a fabricated name.
    return normalized.toUpperCase();
}
